import { createApp, nextTick } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import '@fontsource/bricolage-grotesque/latin-600.css'
import '@fontsource/bricolage-grotesque/latin-700.css'
import '@fontsource/spline-sans-mono/latin-400.css'
import '@fontsource/spline-sans-mono/latin-500.css'
import './styles/app.css'

declare global {
  interface Window {
    __cquBoot?: { ready(): void; fail(): void; wait(): void }
  }
}

const app = createApp(App)
app.use(createPinia())
app.use(router)
// Keep a real, dependency-free recovery screen until the first route is ready.
router.onError(() => window.__cquBoot?.fail())
let renderFailed = false
let mounted = false
app.config.errorHandler = (error) => {
  renderFailed = true
  console.error(error)
  window.__cquBoot?.fail()
}
router.beforeEach(() => window.__cquBoot?.wait())
router.afterEach((_to, _from, failure) => {
  if (!failure)
    void nextTick().then(() => {
      if (mounted && !renderFailed) window.__cquBoot?.ready()
    })
})
// Production CSS loads without blocking the small recovery screen. The full UI
// mounts only after its stylesheet succeeds, so it never flashes unstyled.
const pageStyles = Promise.all(
  Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[data-app-style]'),
  ).map((link) => {
    if (link.sheet) return Promise.resolve()
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup()
        reject(new Error('Style load timeout'))
      }, 20000)
      const cleanup = () => {
        clearTimeout(timer)
        link.removeEventListener('load', loaded)
        link.removeEventListener('error', failed)
      }
      const loaded = () => {
        cleanup()
        resolve()
      }
      const failed = () => {
        cleanup()
        reject(new Error('Style load failed'))
      }
      link.addEventListener('load', loaded)
      link.addEventListener('error', failed)
    })
  }),
)
Promise.all([router.isReady(), pageStyles])
  .then(() => {
    app.mount('#app')
    mounted = true
    if (!renderFailed) window.__cquBoot?.ready()
  })
  .catch(() => window.__cquBoot?.fail())
